"use client";

import React, { useEffect, useState } from "react";
import axiosInstance from "../config/axiosConfig";
import { formatMoney } from "../utils/utils";
import html2canvas from "html2canvas";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";

// Necesario para que jsPDF (cargado via CDN en index.html) detecte html2canvas al usar .html()
window.html2canvas = html2canvas;

const parseFechaLocal = (iso) => {
    const [y, m, d] = iso.split("-").map(Number);
    return new Date(y, m - 1, d, 0, 0, 0, 0);
};

const formatearMes = (periodoStr) => {
    if (!periodoStr) return "";
    const [y, m] = periodoStr.split("-");
    const meses = ["Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio", "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre"];
    return `${meses[parseInt(m, 10) - 1]} - ${y}`;
};

// helper to format date
const formatearFecha = (fechaISO) => {
    if (!fechaISO) return "";
    const fecha = parseFechaLocal(fechaISO);
    const formatter = new Intl.DateTimeFormat("es-CL", { weekday: "long" });
    const dia = formatter.format(fecha);
    const [y, m, d] = fechaISO.split("-");
    const labelDia = dia.charAt(0).toUpperCase() + dia.slice(1);
    const diaCorto = labelDia.split(',')[0]; 
    return `${diaCorto} ${d}-${m}`;
};

const getPeriodoKey = (p) => {
    if (!p) return 'Día';
    const lower = p.toString().toLowerCase();
    return (lower === 'día' || lower === 'dia' || lower === '1') ? 'Día' : 'Noche';
};

const FIRMA_DEFAULT = `
    <table cellpadding="0" cellspacing="0" border="0" style="font-family: Arial, Helvetica, sans-serif; font-size: 13px; color:#333; line-height: 1.35;">
      <tr>
        <!-- Datos -->
        <td style="padding: 0 18px 0 0; vertical-align: top; white-space: nowrap;">
          <div style="font-size: 14px; font-weight: 700; color:#2b2b2b;">M. Angélica Palma P.</div>
          <div style="color:#666;">Directora Administrativa</div>

          <div style="margin-top: 6px; font-weight: 700; letter-spacing: .2px;">SK MEDICAL CARE</div>

          <div style="margin-top: 6px;">
            <span style="color:#666;">Móvil:</span>
            <a href="tel:+5692305037" style="color:#0b74b8; text-decoration: none;">+56 9 2305 037</a>
          </div>

          <div style="margin-top: 4px;">
            <span style="color:#666;">Dirección:</span> El Trovador 4280 Of 403 LC
          </div>

          <div style="margin-top: 6px;">
            <a href="https://skmc.cl" style="color:#0b74b8; text-decoration: none;">www.skmc.cl</a>
          </div>
        </td>

        <!-- Logo -->
        <td style="vertical-align: middle; padding: 0; border-left: 1px solid #e6e6e6; padding-left: 18px;">
          <a href="https://skmc.cl" style="text-decoration:none;">
            <img
              src="https://skmc.cl/wp-content/uploads/2024/10/sk-logo.png"
              alt="SK Medical Care"
              style="display:block; border:0; outline:none; text-decoration:none; height:90px; width:auto;"
            />
          </a>
        </td>
      </tr>
    </table>
`;

const CobranzaDetalle = ({ visible, onClose, periodo, pacienteID, pacienteNombre, modoNotificacion, onNotificacionEnviada, pacienteData }) => {
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState(null);

    // Estados para notificar
    const [showEmailForm, setShowEmailForm] = useState(false);
    const [notificationType, setNotificationType] = useState("1");
    const [emailMessage, setEmailMessage] = useState("");
    const [emailDestinatarios, setEmailDestinatarios] = useState("");
    const [emailAsunto, setEmailAsunto] = useState("");
    const [emailAdjuntosSelect, setEmailAdjuntosSelect] = useState(null);
    const [fileBase64, setFileBase64] = useState(null);

    const handleCopiarImagen = async () => {
        const element = document.getElementById("detalle-cobranza-contenido");
        if (!element) return;
        try {
            const canvas = await html2canvas(element, { scale: 1, backgroundColor: "#ffffff" });
            canvas.toBlob((blob) => {
                if (blob) {
                    navigator.clipboard.write([new ClipboardItem({ "image/png": blob })])
                        .then(() => alert("Imagen copiada al portapapeles."))
                        .catch(err => console.error("Error al copiar:", err));
                }
            });
        } catch (error) {
            console.error("Error imagen:", error);
        }
    };

    const handleDescargarPDF = async () => {
        const element = document.getElementById("detalle-cobranza-contenido");
        if (!element) return;
        try {
            setLoading(true);
            const canvas = await html2canvas(element, { scale: 1.5, useCORS: true, backgroundColor: "#ffffff" });
            const base64image = canvas.toDataURL("image/jpeg", 0.7);
            
            // Crear PDF a partir del canvas con múltiples páginas y márgenes
            const doc = new window.jspdf.jsPDF('p', 'mm', 'a4', true); // compress: true
            const pageWidth = doc.internal.pageSize.getWidth();
            const pageHeight = doc.internal.pageSize.getHeight();
            const marginX = 10;
            const marginY = 10;
            const pdfWidth = pageWidth - (marginX * 2);
            const pdfContentHeight = pageHeight - (marginY * 2);
            
            const pdfHeight = (canvas.height * pdfWidth) / canvas.width;
            let heightLeft = pdfHeight;
            let position = marginY;

            doc.addImage(base64image, 'JPEG', marginX, position, pdfWidth, pdfHeight, undefined, 'FAST');
            heightLeft -= pdfContentHeight;
            
            // Ocultar desbordamiento en la primera página
            doc.setFillColor(255, 255, 255);
            doc.rect(0, pageHeight - marginY, pageWidth, marginY, 'F');

            while (heightLeft > 0) {
                position -= pdfContentHeight;
                doc.addPage();
                doc.addImage(base64image, 'JPEG', marginX, position, pdfWidth, pdfHeight, undefined, 'FAST');
                
                // Ocultar margen superior
                doc.setFillColor(255, 255, 255);
                doc.rect(0, 0, pageWidth, marginY, 'F');
                // Ocultar margen inferior
                doc.rect(0, pageHeight - marginY, pageWidth, marginY, 'F');
                
                heightLeft -= pdfContentHeight;
            }

            doc.save(`Detalle_Cobranza_${pacienteNombre.replace(/\s+/g, '_')}_${periodo}.pdf`);
        } catch (error) {
            console.error("Error al generar PDF:", error);
            alert("No se pudo generar el PDF");
        } finally {
            setLoading(false);
        }
    };

    const handleDescargarPDF_HTML = async () => {
        if (!data) return;
        try {
            setLoading(true);
            const doc = new window.jspdf.jsPDF('p', 'mm', 'a4');
            
            // Título y Encabezados info
            doc.setFontSize(16);
            doc.setTextColor(40);
            doc.text(`Detalle de Cobranza - ${pacienteNombre}`, 14, 15);
            
            doc.setFontSize(10);
            doc.setTextColor(100);
            doc.text(`Periodo: ${formatearMes(periodo)}`, 14, 22);
            doc.text(`Paciente: ${pacienteNombre}`, 14, 27);
            
            // --- NUEVA SECCIÓN: TOTALES DEL PERIODO ---
            doc.setDrawColor(200);
            doc.line(14, 30, 196, 30);
            
            doc.setFontSize(9);
            doc.setTextColor(0);
            
            const totalTurnosBaseFS = (data.totales_turnos?.valor_turnos || 0) + 
                                     (data.totales_adicionales?.adicionales_turno || 0) + 
                                     (data.totales_adicionales?.recargo_fs_turno || 0);
            
            const totalBeneficios = (data.totales_turnos?.colacion || 0) + 
                                   (data.totales_adicionales?.adicionales_colacion || 0) + 
                                   (data.totales_adicionales?.recargo_fs_colacion || 0) +
                                   (data.totales_turnos?.locomocion || 0) + 
                                   (data.totales_adicionales?.adicionales_locomocion || 0) + 
                                   (data.totales_adicionales?.recargo_fs_locomocion || 0);
            
            const totalOtrosHE = (data.totales_turnos?.otro || 0) + 
                                (data.totales_adicionales?.adicionales_otro || 0) + 
                                (data.totales_adicionales?.recargo_fs_otro || 0) +
                                (data.totales_adicionales?.adicionales_horas_extras || 0);

            doc.setFont(undefined, 'bold');
            doc.text("Total Turnos (Base + FS):", 14, 36);
            doc.setFont(undefined, 'normal');
            doc.text(`$${formatMoney(totalTurnosBaseFS)}`, 60, 36);

            doc.setFont(undefined, 'bold');
            doc.text("Beneficios (Alim. + Mov.):", 14, 41);
            doc.setFont(undefined, 'normal');
            doc.text(`$${formatMoney(totalBeneficios)}`, 60, 41);

            doc.setFont(undefined, 'bold');
            doc.text("Otros / Horas Extras:", 110, 36);
            doc.setFont(undefined, 'normal');
            doc.text(`$${formatMoney(totalOtrosHE)}`, 150, 36);

            doc.setFontSize(11);
            doc.setFont(undefined, 'bold');
            doc.setTextColor(0, 128, 0); // Verde para el total
            doc.text("TOTAL DEL PERIODO:", 110, 43);
            doc.text(`$${formatMoney(data.total_general || 0)}`, 155, 43);
            
            doc.setTextColor(0);
            doc.setFontSize(10);
            doc.setFont(undefined, 'normal');
            // -------------------------------------------

            // Preparar Datos para la Tabla
            const tableHeaders = ["Fecha", "Turno", ...auxiliares, "Total Día"];
            const tableRows = [];

            fechas.forEach(f => {
                ['Día', 'Noche'].forEach(pKey => {
                    const row = new Array(tableHeaders.length).fill("-");
                    row[0] = f;
                    row[1] = pKey;
                    
                    let rowTotal = 0;
                    let hasData = false;

                    auxiliares.forEach(aux => {
                        const cell = matrix[f][pKey][aux] || { turno: 0, colacion: 0, movilizacion: 0, he: 0, adicional: 0, esFS: false };
                        
                        const tVal = (Number(cell.turno) || 0);
                        const aVal = (Number(cell.colacion) || 0);
                        const mVal = (Number(cell.movilizacion) || 0);
                        const hVal = (Number(cell.he) || 0);
                        const oVal = (Number(cell.adicional) || 0);
                        const totalCell = tVal + aVal + mVal + hVal + oVal;

                        if (totalCell > 0) {
                            let detail = [];
                            if (tVal > 0) detail.push(`Turno: $${formatMoney(tVal)}`);
                            if (cell.esFS) detail.push(`(F.SANTIAGO)`);
                            if (aVal > 0) detail.push(`Alim: $${formatMoney(aVal)}`);
                            if (mVal > 0) detail.push(`Mov: $${formatMoney(mVal)}`);
                            if (hVal > 0) detail.push(`HE: $${formatMoney(hVal)}`);
                            if (oVal > 0) detail.push(`Otros: $${formatMoney(oVal)}`);
                            
                            row[tableHeaders.indexOf(aux)] = detail.join("\n");
                            rowTotal += totalCell;
                            hasData = true;
                        }
                    });

                    if (hasData) {
                        row[tableHeaders.length - 1] = `$${formatMoney(rowTotal)}`;
                        tableRows.push(row);
                    }
                });
            });

            // Fila de totales de la tabla
            const totalCols = new Array(tableHeaders.length).fill("");
            totalCols[0] = "TOTALES";
            let grandTotalTable = 0;
            auxiliares.forEach(a => {
                const totalAux = fechas.reduce((sum, f) => {
                    const d = matrix[f]['Día'][a] || {};
                    const n = matrix[f]['Noche'][a] || {};
                    const dSum = (Number(d.turno)||0) + (Number(d.colacion)||0) + (Number(d.movilizacion)||0) + (Number(d.he)||0) + (Number(d.adicional)||0);
                    const nSum = (Number(n.turno)||0) + (Number(n.colacion)||0) + (Number(n.movilizacion)||0) + (Number(n.he)||0) + (Number(n.adicional)||0);
                    return sum + dSum + nSum;
                }, 0);
                totalCols[tableHeaders.indexOf(a)] = `$${formatMoney(totalAux)}`;
                grandTotalTable += totalAux;
            });
            totalCols[tableHeaders.length - 1] = `$${formatMoney(grandTotalTable)}`;

            doc.autoTable({
                head: [tableHeaders],
                body: tableRows,
                startY: 50,
                theme: 'grid',
                styles: { fontSize: 7, cellPadding: 1.5 },
                headStyles: { fillColor: [43, 43, 43], textColor: 255 },
                columnStyles: {
                    0: { cellWidth: 20 },
                    1: { cellWidth: 15 }
                },
                foot: [totalCols],
                footStyles: { fillColor: [220, 220, 220], textColor: 0, fontStyle: 'bold' },
                showFoot: 'lastPage'
            });

            doc.save(`Detalle_Cobranza_LIGERO_${pacienteNombre.replace(/\s+/g, '_')}_${periodo}.pdf`);
        } catch (error) {
            console.error("Error al generar PDF Ligero:", error);
            alert("No se pudo generar el PDF ligero.");
        } finally {
            setLoading(false);
        }
    };

    const handleEnviarCorreo = async () => {
        const element = document.getElementById("detalle-cobranza-contenido");
        if (!element || !data) {
            alert("No se pudo cargar el detalle para enviarlo");
            return;
        }
        try {
            setLoading(true);
            
            // 1. Capturamos la imagen para el cuerpo del correo (CID) - Calidad baja para velocidad
            const canvas = await html2canvas(element, { scale: 1.2, useCORS: true, backgroundColor: "#ffffff" });
            const base64image = canvas.toDataURL("image/jpeg", 0.6);

            // 2. Generamos el PDF LIGERO para adjuntar (usando la misma lógica de handleDescargarPDF_HTML)
            const doc = new window.jspdf.jsPDF('p', 'mm', 'a4');
            doc.setFontSize(16);
            doc.setTextColor(40);
            doc.text(`Detalle de Cobranza - ${pacienteNombre}`, 14, 15);
            doc.setFontSize(10);
            doc.setTextColor(100);
            doc.text(`Periodo: ${formatearMes(periodo)}`, 14, 22);
            doc.text(`Paciente: ${pacienteNombre}`, 14, 27);
            doc.setDrawColor(200);
            doc.line(14, 30, 196, 30);
            
            const totalTurnosBaseFS = (data.totales_turnos?.valor_turnos || 0) + 
                                     (data.totales_adicionales?.adicionales_turno || 0) + 
                                     (data.totales_adicionales?.recargo_fs_turno || 0);
            const totalBeneficios = (data.totales_turnos?.colacion || 0) + 
                                   (data.totales_adicionales?.adicionales_colacion || 0) + 
                                   (data.totales_adicionales?.recargo_fs_colacion || 0) +
                                   (data.totales_turnos?.locomocion || 0) + 
                                   (data.totales_adicionales?.adicionales_locomocion || 0) + 
                                   (data.totales_adicionales?.recargo_fs_locomocion || 0);
            const totalOtrosHE = (data.totales_turnos?.otro || 0) + 
                                (data.totales_adicionales?.adicionales_otro || 0) + 
                                (data.totales_adicionales?.recargo_fs_otro || 0) +
                                (data.totales_adicionales?.adicionales_horas_extras || 0);

            doc.setFontSize(9);
            doc.setTextColor(0);
            doc.setFont(undefined, 'bold');
            doc.text("Total Turnos (Base + FS):", 14, 36);
            doc.setFont(undefined, 'normal');
            doc.text(`$${formatMoney(totalTurnosBaseFS)}`, 60, 36);
            doc.setFont(undefined, 'bold');
            doc.text("Beneficios (Alim. + Mov.):", 14, 41);
            doc.setFont(undefined, 'normal');
            doc.text(`$${formatMoney(totalBeneficios)}`, 60, 41);
            doc.setFont(undefined, 'bold');
            doc.text("Otros / Horas Extras:", 110, 36);
            doc.setFont(undefined, 'normal');
            doc.text(`$${formatMoney(totalOtrosHE)}`, 150, 36);
            doc.setFontSize(11);
            doc.setFont(undefined, 'bold');
            doc.setTextColor(0, 128, 0);
            doc.text("TOTAL DEL PERIODO:", 110, 43);
            doc.text(`$${formatMoney(data.total_general || 0)}`, 155, 43);

            const tableHeaders = ["Fecha", "Turno", ...auxiliares, "Total Día"];
            const tableRows = [];
            fechas.forEach(f => {
                ['Día', 'Noche'].forEach(pKey => {
                    const row = new Array(tableHeaders.length).fill("-");
                    row[0] = f;
                    row[1] = pKey;
                    let rowTotal = 0;
                    let hasData = false;
                    auxiliares.forEach(aux => {
                        const cell = matrix[f][pKey][aux] || { turno: 0, colacion: 0, movilizacion: 0, he: 0, adicional: 0, esFS: false };
                        const tVal = (Number(cell.turno) || 0);
                        const aVal = (Number(cell.colacion) || 0);
                        const mVal = (Number(cell.movilizacion) || 0);
                        const hVal = (Number(cell.he) || 0);
                        const oVal = (Number(cell.adicional) || 0);
                        const totalCell = tVal + aVal + mVal + hVal + oVal;
                        if (totalCell > 0) {
                            let detail = [];
                            if (tVal > 0) detail.push(`Turno: $${formatMoney(tVal)}`);
                            if (cell.esFS) detail.push(`(F.SANTIAGO)`);
                            if (aVal > 0) detail.push(`Alim: $${formatMoney(aVal)}`);
                            if (mVal > 0) detail.push(`Mov: $${formatMoney(mVal)}`);
                            if (hVal > 0) detail.push(`HE: $${formatMoney(hVal)}`);
                            if (oVal > 0) detail.push(`Otros: $${formatMoney(oVal)}`);
                            row[tableHeaders.indexOf(aux)] = detail.join("\n");
                            rowTotal += totalCell;
                            hasData = true;
                        }
                    });
                    if (hasData) {
                        row[tableHeaders.length - 1] = `$${formatMoney(rowTotal)}`;
                        tableRows.push(row);
                    }
                });
            });

            const totalCols = new Array(tableHeaders.length).fill("");
            totalCols[0] = "TOTALES";
            let grandTotalTable = 0;
            auxiliares.forEach(a => {
                const totalAux = fechas.reduce((sum, f) => {
                    const d = matrix[f]['Día'][a] || {};
                    const n = matrix[f]['Noche'][a] || {};
                    const dSum = (Number(d.turno)||0) + (Number(d.colacion)||0) + (Number(d.movilizacion)||0) + (Number(d.he)||0) + (Number(d.adicional)||0);
                    const nSum = (Number(n.turno)||0) + (Number(n.colacion)||0) + (Number(n.movilizacion)||0) + (Number(n.he)||0) + (Number(n.adicional)||0);
                    return sum + dSum + nSum;
                }, 0);
                totalCols[tableHeaders.indexOf(a)] = `$${formatMoney(totalAux)}`;
                grandTotalTable += totalAux;
            });
            totalCols[tableHeaders.length - 1] = `$${formatMoney(grandTotalTable)}`;

            doc.autoTable({
                head: [tableHeaders],
                body: tableRows,
                startY: 50,
                theme: 'grid',
                styles: { fontSize: 7, cellPadding: 1.5 },
                headStyles: { fillColor: [43, 43, 43], textColor: 255 },
                columnStyles: { 0: { cellWidth: 20 }, 1: { cellWidth: 15 } },
                foot: [totalCols],
                footStyles: { fillColor: [220, 220, 220], textColor: 0, fontStyle: 'bold' },
                showFoot: 'lastPage'
            });

            const pdfBase64 = doc.output('datauristring');

            const adjuntos = [
                {
                    name: `Detalle_Cobranza_${pacienteNombre.replace(/\s+/g, '_')}_${periodo}.pdf`,
                    data: pdfBase64.split(",")[1],
                    mime: 'application/pdf'
                }
            ];

            if (emailAdjuntosSelect && fileBase64) {
                adjuntos.push({
                    name: emailAdjuntosSelect.name,
                    data: fileBase64.split(",")[1],
                    mime: emailAdjuntosSelect.type || 'application/octet-stream'
                });
            }

            await axiosInstance.post("/acuerdo-mutuo/cobranza/notificar", {
                pacienteID,
                periodo,
                numeroNotificacion: notificationType,
                destinatarios: emailDestinatarios,
                asunto: emailAsunto,
                mensaje: emailMessage + `<br/><br/>` + FIRMA_DEFAULT + `<br/><br/><img src="${base64image}" style="max-width:100%; border:1px solid #ddd; border-radius:8px;"/>`,
                adjuntos: adjuntos
            });

            alert(`Notificación ${notificationType} enviada correctamente.`);
            if (onNotificacionEnviada) onNotificacionEnviada(notificationType);
            onClose();
        } catch (error) {
            console.error("Error al enviar correo:", error);
            alert("Ocurrió un error al enviar la notificación.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (visible) {
            setShowEmailForm(!!modoNotificacion);
            
            // Auto detect notification number (counter)
            const prevNum = parseInt(pacienteData?.numero_notificacion || "0", 10);
            const nextNotif = (prevNum + 1).toString();
            setNotificationType(nextNotif);
            
            const avisoPrefix = `Notificación #${nextNotif}`;
            setEmailMessage(`<p>Estimado/a,</p><p>Se adjunta el detalle de cobranza correspondiente al periodo de ${formatearMes(periodo)} (${avisoPrefix}).</p><p>Quedamos atentos a cualquier consulta.</p><p>Saludos cordiales.</p>`);
            
            if (pacienteData) {
                // El tutor de pago viene ahora desde el backend como correo_tutor
                setEmailDestinatarios(pacienteData.correo_tutor || pacienteData.tutor_correo || pacienteData.correo || "");
            }
            setEmailAsunto(`Cobranza SKMC - ${formatearMes(periodo)} - ${pacienteNombre}`);
            setEmailAdjuntosSelect(null);
            setFileBase64(null);
        }
    }, [visible, modoNotificacion, periodo, pacienteData, pacienteNombre]);

    // Cuando el detalle carga (async), actualiza el destinatario con el correo del tutor
    useEffect(() => {
        if (data?.tutor?.correo && modoNotificacion) {
            setEmailDestinatarios(data.tutor.correo);
        }
    }, [data, modoNotificacion]);

    useEffect(() => {
        const cargarDetalle = async () => {
            try {
                setLoading(true);
                const resp = await axiosInstance.get("/acuerdo-mutuo/cobranza/detalle", {
                    params: { fecha: periodo, pacienteID }
                });
                setData(resp.data.data);
            } catch (err) {
                console.error(err);
            } finally {
                setLoading(false);
            }
        };

        if (visible && periodo && pacienteID) {
            cargarDetalle();
        } else {
            setData(null);
        }
    }, [visible, periodo, pacienteID]);

    if (!visible) return null;

    // ----- CONSTRUCCIÓN DE LA MATRIZ -----
    let auxiliares = [];
    let fechas = [];
    let matrix = {};

    if (data) {
        // 1. Extraer todos los auxiliares únicos
        const auxiliaresSet = new Set();
        (data.turnos || []).forEach(t => auxiliaresSet.add(t.auxiliar_nombre));
        (data.adicionales || []).forEach(a => auxiliaresSet.add(a.auxiliar_reemplazo_nombre || a.auxiliar_original_nombre || ""));
        auxiliares = Array.from(auxiliaresSet).filter(Boolean).sort();

        // 2. Extraer todas las fechas únicas
        const fechasSet = new Set();
        (data.turnos || []).forEach(t => fechasSet.add(t.fecha));
        (data.adicionales || []).forEach(a => {
            if (a.fecha) fechasSet.add(a.fecha);
            if (a.fechaAdicional) fechasSet.add(a.fechaAdicional);
        });
        fechas = Array.from(fechasSet).filter(Boolean).sort(); 

        // 3. Inicializar matriz
        fechas.forEach(f => {
            matrix[f] = { 'Día': {}, 'Noche': {} };
            auxiliares.forEach(aux => {
                matrix[f]['Día'][aux] = { turno: 0, colacion: 0, movilizacion: 0, he: 0, adicional: 0, motivos: [] };
                matrix[f]['Noche'][aux] = { turno: 0, colacion: 0, movilizacion: 0, he: 0, adicional: 0, motivos: [] };
            });
        });

        // 4. Poblar matriz con turnos
        (data.turnos || []).forEach(t => {
            const f = t.fecha;
            const p = getPeriodoKey(t.periodo);
            const a = t.auxiliar_nombre;
            if (f && matrix[f] && matrix[f][p] && a) {
                matrix[f][p][a].turno += (Number(t.valor_turno_paciente) || 0);
                matrix[f][p][a].colacion += (Number(t.colacion_paciente) || 0);
                matrix[f][p][a].movilizacion += (Number(t.locomocion_paciente) || 0);
                matrix[f][p][a].adicional += (Number(t.otro_paciente) || 0);
            }
        });

        // 5. Poblar matriz con adicionales
        (data.adicionales || []).forEach(ad => {
            const f = ad.fecha || ad.fechaAdicional;
            const p = getPeriodoKey(ad.periodo || ad.tipo_fecha);
            let a = ad.auxiliar_reemplazo_nombre || ad.auxiliar_original_nombre || "";
            
            // Si el nombre viene vacío (ej: auxiliarID 0), intentamos asociarlo al turno que ya exista ese día
            if (!a && f && p) {
                const turnoAsociado = (data.turnos || []).find(t => t.fecha === f && getPeriodoKey(t.periodo) === p);
                if (turnoAsociado) {
                    a = turnoAsociado.auxiliar_nombre;
                } else {
                    // Si no hay turno, buscamos cualquier auxiliar del set para no perder el dato
                    a = auxiliares[0] || "General";
                }
            }

            if (f && matrix[f] && matrix[f][p] && a) {
                if (!matrix[f][p][a]) {
                    matrix[f][p][a] = { turno: 0, colacion: 0, movilizacion: 0, he: 0, adicional: 0, motivos: [], esFS: false };
                }
                
                // Sumamos recargos FS y base de adicional (si tiene) a las líneas base para "pisar" el valor
                matrix[f][p][a].turno += (Number(ad.valor_turno_paciente) || 0) + (Number(ad.recargo_fs_turno) || 0);
                matrix[f][p][a].he += (Number(ad.horas_extras_paciente) || 0);
                matrix[f][p][a].colacion += (Number(ad.colacion_paciente) || 0) + (Number(ad.recargo_fs_colacion) || 0);
                matrix[f][p][a].movilizacion += (Number(ad.locomocion_paciente) || 0) + (Number(ad.recargo_fs_locomocion) || 0);
                
                // Solo dejamos como "adicional" lo que sea verdaderamente un monto extra (Otro)
                matrix[f][p][a].adicional += (Number(ad.otro_paciente) || 0) + (Number(ad.recargo_fs_otro) || 0);
                
                if (ad.fuera_stgo) matrix[f][p][a].esFS = true;
                if (ad.motivo && ad.motivo.trim()) {
                    matrix[f][p][a].motivos.push(ad.motivo.trim());
                }
            }
        });
    }

    return (
        <div className="modal fade show d-block" style={{ backgroundColor: "rgba(0,0,0,0.5)", overflowY: "auto" }}>
            <div className="modal-dialog modal-xl modal-dialog-centered">
                <div className="modal-content">
                    <div className="modal-header d-flex justify-content-between align-items-center">
                        <h5 className="modal-title">
                            Detalle de Cobranza - {pacienteNombre} ({formatearMes(periodo)})
                        </h5>
                        <div>
                            <button 
                                className={`btn btn-sm ${showEmailForm ? 'btn-outline-danger' : 'btn-outline-success'} mx-2`} 
                                onClick={() => setShowEmailForm(!showEmailForm)}
                                disabled={loading}
                            >
                                {showEmailForm ? (
                                    <><i className="bi bi-x-circle me-1"></i> Cerrar Correo</>
                                ) : (
                                    <><i className="bi bi-envelope me-1"></i> Enviar por Correo</>
                                )}
                            </button>
                            <button className="btn-close" style={{marginLeft: 'auto'}} onClick={onClose}></button>
                        </div>
                    </div>

                    {/* FORMULARIO DE CORREO (Se mantiene fuera del 'detalle-cobranza-contenido' para que no salga en la captura) */}
                    {showEmailForm && (
                        <div className="bg-light border-bottom border-primary p-3">
                            <h6 className="text-primary fw-bold mb-3"><i className="bi bi-envelope-paper me-2"></i> Notificar a Paciente</h6>

                            {(data?.tutor?.nombre || pacienteData?.tutor_nombre || data?.paciente?.fecha_inicio_acuerdo || pacienteData?.fecha_inicio_acuerdo) && (
                                <div className="mb-3 d-flex gap-3">
                                    {/* SECCIÓN IZQUIERDA: TUTOR */}
                                    <div className="flex-fill p-2 bg-white border-start border-primary border-4 rounded shadow-sm">
                                        <span className="text-muted small fw-bold text-uppercase d-block mb-1">Tutor:</span>
                                        <span className="text-dark fw-bold fs-6">
                                            {data?.tutor?.nombre || pacienteData?.tutor_nombre || 'Sin tutor'}
                                        </span>
                                        {(data?.tutor?.parentesco || pacienteData?.tutor_parentesco) && (
                                            <span className="badge bg-light text-primary border ms-2">
                                                <i className="bi bi-people me-1"></i>
                                                {data?.tutor?.parentesco || pacienteData?.tutor_parentesco}
                                            </span>
                                        )}
                                    </div>
                                    {/* SECCIÓN DERECHA: FECHA INICIO PACIENTE */}
                                    <div className="flex-fill p-2 bg-white border-start border-success border-4 rounded shadow-sm">
                                        <span className="text-muted small fw-bold text-uppercase d-block mb-1">Fecha Inicio Paciente:</span>
                                        <span className="text-dark fw-bold fs-6">
                                            {(data?.paciente?.fecha_inicio_acuerdo || pacienteData?.fecha_inicio_acuerdo) ? ((data?.paciente?.fecha_inicio_acuerdo || pacienteData?.fecha_inicio_acuerdo).split(" ")[0].split("-").reverse().join("-")) : '-'}
                                        </span>
                                    </div>
                                </div>
                            )}
                            <div className="row g-3">
                                <div className="col-md-12">
                                    <label className="form-label fw-bold text-dark small">Destinatarios (separados por coma)</label>
                                    <input 
                                        type="text" 
                                        className="form-control" 
                                        value={emailDestinatarios}
                                        onChange={(e) => setEmailDestinatarios(e.target.value)}
                                        disabled={loading}
                                        placeholder="correo@ejemplo.com, otro@ejemplo.com"
                                    />
                                </div>
                                <div className="col-md-4">
                                    <label className="form-label fw-bold text-dark small">Número de Notificación (Autoincremental)</label>
                                    <div className="input-group">
                                        <span className="input-group-text bg-primary text-white fw-bold">#</span>
                                        <input 
                                            type="text" 
                                            className="form-control fw-bold bg-white text-center" 
                                            value={notificationType} 
                                            readOnly 
                                            disabled
                                        />
                                    </div>
                                    <div className="mt-3 text-muted small">
                                        <i className="bi bi-paperclip me-1"></i>
                                        El detalle de abajo se adjuntará como imagen al cuerpo del correo y como PDF adjunto.
                                    </div>
                                    <div className="mt-4">
                                        <label className="form-label fw-bold text-dark small">Adjuntar archivo extra (Opcional)</label>
                                        <input 
                                            type="file" 
                                            className="form-control form-control-sm"
                                            onChange={(e) => {
                                                if (e.target.files.length > 0) {
                                                    const f = e.target.files[0];
                                                    setEmailAdjuntosSelect(f);
                                                    const reader = new FileReader();
                                                    reader.onload = () => {
                                                        setFileBase64(reader.result);
                                                    };
                                                    reader.readAsDataURL(f);
                                                } else {
                                                    setEmailAdjuntosSelect(null);
                                                    setFileBase64(null);
                                                }
                                            }}
                                            disabled={loading}
                                        />
                                    </div>
                                </div>
                                <div className="col-md-8">
                                    <label className="form-label fw-bold text-dark small">Asunto</label>
                                    <input 
                                        type="text" 
                                        className="form-control mb-3" 
                                        value={emailAsunto}
                                        onChange={(e) => setEmailAsunto(e.target.value)}
                                        disabled={loading}
                                    />
                                    <label className="form-label fw-bold text-dark small">Mensaje al Paciente</label>
                                    <div style={{ backgroundColor: '#fff', borderRadius: '4px' }}>
                                        <ReactQuill 
                                            theme="snow"
                                            value={emailMessage}
                                            onChange={setEmailMessage}
                                            readOnly={loading}
                                            modules={{
                                                toolbar: [
                                                    ['bold', 'italic', 'underline', 'strike'],
                                                    [{ 'list': 'ordered'}, { 'list': 'bullet' }],
                                                    [{ 'color': [] }, { 'background': [] }],
                                                ]
                                            }}
                                            style={{ height: '150px', marginBottom: '50px' }}
                                        />
                                    </div>
                                    <div className="mt-2 p-3 bg-white border rounded shadow-sm" style={{ opacity: 0.85 }}>
                                        <div className="small text-muted mb-2"><i className="bi bi-info-circle me-1"></i> Firma Automática (No editable):</div>
                                        <div dangerouslySetInnerHTML={{ __html: FIRMA_DEFAULT }} />
                                    </div>
                                </div>
                            </div>
                            <div className="mt-4 text-end">
                                <button 
                                    className="btn btn-primary px-4 fw-bold shadow-sm" 
                                    onClick={handleEnviarCorreo}
                                    disabled={loading}
                                >
                                    {loading ? (
                                        <><span className="spinner-border spinner-border-sm me-2"></span>Enviando Correo...</>
                                    ) : (
                                        <><i className="bi bi-send me-2"></i>Enviar Notificación</>
                                    )}
                                </button>
                            </div>
                        </div>
                    )}

                    <div className="modal-body" id="detalle-cobranza-contenido">
                        {loading && (
                            <div className="text-center py-5">
                                <div className="spinner-border text-primary"></div>
                                <p className="mt-3">Cargando detalle...</p>
                            </div>
                        )}

                        {!loading && data && (
                            <>
                                <div className="text-center mb-4">
                                    <h4 className="fw-bold text-dark mb-1">{pacienteNombre}</h4>
                                    <p className="text-muted small text-uppercase fw-bold m-0">{formatearMes(periodo)}</p>
                                </div>

                                <div className="mb-4">
                                    <div className="row g-3">
                                        <div className="col-md-4">
                                            <div className="p-2 bg-white border-start border-danger border-4 rounded shadow-sm text-center border overflow-hidden h-100">
                                                <span className="text-muted small fw-bold text-uppercase d-block mb-1">Saldo Anterior</span>
                                                <span className="text-danger fw-bold fs-5">${formatMoney(data?.saldo_anterior || 0)}</span>
                                            </div>
                                        </div>
                                        <div className="col-md-4">
                                            <div className="p-2 bg-white border-start border-primary border-4 rounded shadow-sm text-center border overflow-hidden h-100">
                                                <span className="text-muted small fw-bold text-uppercase d-block mb-1">Consolidado Cartola</span>
                                                <span className={`fw-bold fs-5 ${Number(data?.saldo_cartola) > 0 ? 'text-danger' : 'text-primary'}`}>
                                                    ${formatMoney(data?.saldo_cartola || 0)}
                                                </span>
                                            </div>
                                        </div>
                                        <div className="col-md-4">
                                            <div className="p-2 bg-white border-start border-success border-4 rounded shadow-sm text-center border overflow-hidden h-100">
                                                <span className="text-muted small fw-bold text-uppercase d-block mb-1">TOTAL DEL MES</span>
                                                <span className="text-success fw-bold fs-5">${formatMoney(data?.total_general || 0)}</span>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div className="p-4 mb-4 border shadow-sm" style={{ background: "#f8f9fa", borderRadius: "12px", borderLeft: "5px solid #0d6efd" }}>
                                    {/* BLOQUE DE CONCEPTOS BASE */}
                                    <div className="row g-3 text-center align-items-stretch mb-3"> {/* Changed align-items-center to align-items-stretch */}
                                        <div className="col-md-3 d-flex"> {/* Added d-flex */}
                                            <div className="p-2 bg-white rounded shadow-sm flex-fill"> {/* Added flex-fill */}
                                                <div className="text-muted small text-uppercase fw-bold mb-1" style={{fontSize: '0.65rem'}}>Total Turnos (Base + FS)</div>
                                                <div className="fs-6 text-dark fw-bold">
                                                    ${formatMoney(
                                                        (data.totales_turnos?.valor_turnos || 0) + 
                                                        (data.totales_adicionales?.adicionales_turno || 0) + 
                                                        (data.totales_adicionales?.recargo_fs_turno || 0)
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                        <div className="col-md-3 d-flex"> {/* Added d-flex */}
                                            <div className="p-2 bg-white rounded shadow-sm flex-fill"> {/* Added flex-fill */}
                                                <div className="text-muted small text-uppercase fw-bold mb-1" style={{fontSize: '0.65rem'}}>Beneficios (Alim. + Mov.)</div>
                                                <div className="fs-6 text-dark fw-bold">
                                                    ${formatMoney(
                                                        (data.totales_turnos?.colacion || 0) + 
                                                        (data.totales_adicionales?.adicionales_colacion || 0) + 
                                                        (data.totales_adicionales?.recargo_fs_colacion || 0) +
                                                        (data.totales_turnos?.locomocion || 0) + 
                                                        (data.totales_adicionales?.adicionales_locomocion || 0) + 
                                                        (data.totales_adicionales?.recargo_fs_locomocion || 0)
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                        <div className="col-md-3 d-flex"> {/* Added d-flex */}
                                            <div className="p-2 bg-white rounded shadow-sm flex-fill"> {/* Added flex-fill */}
                                                <div className="text-muted small text-uppercase fw-bold mb-1" style={{fontSize: '0.65rem'}}>Otros / Horas Extras</div>
                                                <div className="fs-6 text-dark fw-bold">
                                                    ${formatMoney(
                                                        (data.totales_turnos?.otro || 0) + 
                                                        (data.totales_adicionales?.adicionales_otro || 0) + 
                                                        (data.totales_adicionales?.recargo_fs_otro || 0) +
                                                        (data.totales_adicionales?.adicionales_horas_extras || 0)
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                        <div className="col-md-3 d-flex"> {/* Moved this div and added d-flex */}
                                            <div className="p-2 bg-success text-white rounded shadow-sm flex-fill"> {/* Added flex-fill */}
                                                <div className="small text-uppercase fw-bold mb-1" style={{fontSize: '0.65rem'}}>Total del Periodo</div>
                                                <div className="fs-5 fw-bold">${formatMoney(data.total_general || 0)}</div>
                                            </div>
                                        </div>
                                    </div>

                                    {/* SECCIÓN DE AJUSTES (SOLO SI EXISTEN) */}
                                    {(data.otros_suma > 0 || data.otros_resta > 0) && (
                                        <div className="mt-4 p-3 bg-white border rounded shadow-sm mb-3">
                                            <div className="text-uppercase fw-bold text-muted mb-2 border-bottom pb-1" style={{fontSize: '0.75rem'}}>
                                                <i className="bi bi-gear-fill me-2 text-secondary"></i>Ajustes del Periodo
                                            </div>
                                            <div className="d-flex justify-content-around align-items-center">
                                                <div className="text-center">
                                                    <small className="text-muted d-block">Subtotal Periodo</small>
                                                    <span className="fw-bold">${formatMoney((data.total_general || 0) - (data.otros_suma || 0) + (data.otros_resta || 0))}</span>
                                                </div>
                                                <i className="bi bi-chevron-right text-muted"></i>
                                                {data.otros_suma > 0 && (
                                                    <div className="text-center">
                                                        <small className="text-success d-block lh-1">Otros Cargos (+)</small>
                                                        <span className="fw-bold text-success">+${formatMoney(data.otros_suma)}</span>
                                                    </div>
                                                )}
                                                {data.otros_resta > 0 && (
                                                    <div className="text-center">
                                                        <small className="text-danger d-block lh-1">Otros Descuentos (-)</small>
                                                        <span className="fw-bold text-danger">-${formatMoney(data.otros_resta)}</span>
                                                    </div>
                                                )}
                                                <i className="bi bi-arrow-right-circle-fill text-success fs-5"></i>
                                                <div className="text-center">
                                                    <small className="text-muted d-block lh-1">TOTAL FINAL</small>
                                                    <span className="fw-bold text-success fs-5">${formatMoney(data.total_general || 0)}</span>
                                                </div>
                                            </div>
                                        </div>
                                    )}

                                    {/* SECCIÓN DE OBSERVACIÓN (SI EXISTE) */}
                                    {data.observacion && (
                                        <div className="mt-3 p-3 text-dark rounded border shadow-sm" style={{ background: "#fff9db", borderLeft: "5px solid #fcc419 !important" }}>
                                            <div className="fw-bold text-dark mb-1" style={{fontSize: '0.85rem'}}>
                                                <i className="bi bi-chat-left-dots-fill me-2 text-warning"></i>Observaciones y Notas del Cobro:
                                            </div>
                                            <div style={{ fontSize: "0.9rem", whiteSpace: "pre-wrap" }}>
                                                {data.observacion}
                                            </div>
                                        </div>
                                    )}
                                    
                                    <div className="text-center mt-3 pt-2">
                                        <small className="text-muted opacity-75">La siguiente tabla desglosa cada servicio prestado durante el periodo:</small>
                                    </div>
                                </div>

                                {fechas.length > 0 ? (
                                    <div className="table-responsive">
                                        <table className="table table-bordered table-striped mt-3" style={{ fontSize: "0.9rem" }}>
                                            <thead className="table-light align-middle text-center">
                                                <tr>
                                                    <th>Fecha</th>
                                                    <th>Turno</th>
                                                    {auxiliares.map(a => (
                                                        <th key={a} style={{ minWidth: "120px" }}>{a}</th>
                                                    ))}
                                                    <th className="bg-secondary text-white">Total Día</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {(() => {
                                                    const groupFechas = [];
                                                    let currentGroupLabel = "";
                                                    const [perY, perM] = periodo.split('-').map(Number);
                                                    
                                                    fechas.forEach(f => {
                                                        const [yStr, mStr, dStr] = f.split('-');
                                                        let y = parseInt(yStr, 10);
                                                        let m = parseInt(mStr, 10);
                                                        const d = parseInt(dStr, 10);

                                                        let billingM = m;
                                                        let billingY = y;

                                                        if (d >= 26) {
                                                            billingM += 1;
                                                            if (billingM > 12) {
                                                                billingM = 1;
                                                                billingY += 1;
                                                            }
                                                        }

                                                        const isAdelanto = billingY > perY || (billingY === perY && billingM > perM);
                                                        const meses = ["Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio", "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre"];
                                                        
                                                        const label = `${meses[billingM - 1]} - ${billingY} ${isAdelanto ? '(Adelanto)' : ''}`.trim();

                                                        if (label !== currentGroupLabel) {
                                                            groupFechas.push({ type: 'header', label: label });
                                                            currentGroupLabel = label;
                                                        }
                                                        groupFechas.push({ type: 'date', f: f });
                                                    });

                                                    return groupFechas.map((item, index) => {
                                                        if (item.type === 'header') {
                                                            return (
                                                                <tr key={`header-${index}`} className="table-secondary">
                                                                    <td colSpan={3 + auxiliares.length} className="fw-bold text-center text-uppercase fs-6">
                                                                        {item.label}
                                                                    </td>
                                                                </tr>
                                                            );
                                                        }

                                                        const f = item.f;
                                                    const rowDia = matrix[f]['Día'];
                                                    const rowNoche = matrix[f]['Noche'];
                                                    
                                                    const totalDia = auxiliares.reduce((sum, a) => sum + rowDia[a].turno + rowDia[a].he + rowDia[a].colacion + rowDia[a].movilizacion + rowDia[a].adicional, 0);
                                                    const totalNoche = auxiliares.reduce((sum, a) => sum + rowNoche[a].turno + rowNoche[a].he + rowNoche[a].colacion + rowNoche[a].movilizacion + rowNoche[a].adicional, 0);

                                                    // Para no mostrar días completamente vacíos si por alguna razón no tienen montos
                                                    if (totalDia === 0 && totalNoche === 0) return null;

                                                    return (
                                                        <React.Fragment key={f}>
                                                            {/* FILA DÍA */}
                                                            <tr>
                                                                <td rowSpan={2} className="align-middle fw-bold">{formatearFecha(f)}</td>
                                                                <td className="text-danger fw-bold text-center align-middle">Día</td>
                                                                {auxiliares.map(a => {
                                                                    const cell = rowDia[a];
                                                                    const cellTotal = cell.turno + cell.colacion + cell.movilizacion + cell.he + cell.adicional;
                                                                    return (
                                                                        <td key={a} className="text-end" style={{ fontSize: "0.85rem", whiteSpace: "nowrap" }}>
                                                                            {cellTotal > 0 ? (
                                                                                <>
                                                                                    {cell.turno > 0 && <div>Turno {cell.esFS && <span className="badge bg-warning text-dark p-1" style={{fontSize: '0.65rem'}}>FS</span>}: ${formatMoney(cell.turno)}</div>}
                                                                                    {cell.colacion > 0 && <div>Alim.: ${formatMoney(cell.colacion)}</div>}
                                                                                    {cell.movilizacion > 0 && <div>Mov.: ${formatMoney(cell.movilizacion)}</div>}
                                                                                    {cell.he > 0 && <div>HE: ${formatMoney(cell.he)}</div>}
                                                                                     {cell.adicional > 0 && (
                                                                                        <div>
                                                                                            Adic.: ${formatMoney(cell.adicional)}
                                                                                            {cell.motivos && cell.motivos.length > 0 && (
                                                                                                <div className="text-muted" style={{ fontSize: "0.7rem", fontStyle: "italic" }}>
                                                                                                    ({cell.motivos.join(", ")})
                                                                                                </div>
                                                                                            )}
                                                                                        </div>
                                                                                    )}
                                                                                     {/* Total interno removido por solicitud del usuario */}
                                                                                 </>
                                                                             ) : '-'}
                                                                         </td>
                                                                     );
                                                                 })}
                                                                 <td className="text-end fw-bold bg-light align-middle">
                                                                     {totalDia > 0 ? `$${formatMoney(totalDia)}` : '-'}
                                                                 </td>
                                                             </tr>
                                                             {/* FILA NOCHE */}
                                                             <tr>
                                                                 <td className="text-primary fw-bold text-center align-middle">Noche</td>
                                                                 {auxiliares.map(a => {
                                                                     const cell = rowNoche[a];
                                                                     const cellTotal = cell.turno + cell.colacion + cell.movilizacion + cell.he + cell.adicional;
                                                                     return (
                                                                         <td key={a} className="text-end" style={{ fontSize: "0.85rem", whiteSpace: "nowrap" }}>
                                                                             {cellTotal > 0 ? (
                                                                                 <>
                                                                                     {cell.turno > 0 && <div>Turno {cell.esFS && <span className="badge bg-warning text-dark p-1" style={{fontSize: '0.65rem'}}>FS</span>}: ${formatMoney(cell.turno)}</div>}
                                                                                     {cell.colacion > 0 && <div>Alim.: ${formatMoney(cell.colacion)}</div>}
                                                                                     {cell.movilizacion > 0 && <div>Mov.: ${formatMoney(cell.movilizacion)}</div>}
                                                                                     {cell.he > 0 && <div>HE: ${formatMoney(cell.he)}</div>}
                                                                                    {cell.adicional > 0 && (
                                                                                        <div>
                                                                                            Adic. {cell.esFS && <span className="badge bg-warning text-dark p-1" style={{fontSize: '0.65rem'}}>FS</span>}: ${formatMoney(cell.adicional)}
                                                                                            {cell.motivos.length > 0 && (
                                                                                                <div className="text-muted" style={{ fontSize: "0.7rem", fontStyle: "italic" }}>
                                                                                                    ({cell.motivos.join(", ")})
                                                                                                </div>
                                                                                            )}
                                                                                        </div>
                                                                                    )}
                                                                                     {/* Total interno removido por solicitud del usuario */}
                                                                                 </>
                                                                             ) : '-'}
                                                                        </td>
                                                                    );
                                                                })}
                                                                <td className="text-end fw-bold bg-light align-middle">
                                                                    {totalNoche > 0 ? `$${formatMoney(totalNoche)}` : '-'}
                                                                </td>
                                                            </tr>
                                                        </React.Fragment>
                                                    );
                                                    });
                                                })()}

                                                {/* FILA DE TOTALES POR AUXILIAR */}
                                                <tr className="table-dark align-middle">
                                                    <td colSpan={2} className="text-end fw-bold">TOTALES TURNOS:</td>
                                                    {auxiliares.map(a => {
                                                        const totalAux = fechas.reduce((sum, f) => {
                                                            const { turno: d1, colacion: d2, movilizacion: d3, he: dH, adicional: d4 } = matrix[f]['Día'][a];
                                                            const { turno: n1, colacion: n2, movilizacion: n3, he: nH, adicional: n4 } = matrix[f]['Noche'][a];
                                                            return sum + d1 + d2 + d3 + dH + d4 + n1 + n2 + n3 + nH + n4;
                                                        }, 0);
                                                        return (
                                                            <td key={a} className="text-end fw-bold">
                                                                ${formatMoney(totalAux)}
                                                            </td>
                                                        );
                                                    })}
                                                    <td className="text-end fw-bold fs-6">
                                                        ${formatMoney(
                                                            fechas.reduce((sum, f) => 
                                                                sum + auxiliares.reduce((s, a) => {
                                                                    const { turno: d1, colacion: d2, movilizacion: d3, he: dH, adicional: d4 } = matrix[f]['Día'][a];
                                                                    const { turno: n1, colacion: n2, movilizacion: n3, he: nH, adicional: n4 } = matrix[f]['Noche'][a];
                                                                    return s + d1 + d2 + d3 + dH + d4 + n1 + n2 + n3 + nH + n4;
                                                                }, 0)
                                                            , 0)
                                                        )}
                                                    </td>
                                                </tr>
                                            </tbody>
                                        </table>
                                    </div>
                                ) : (
                                    <div className="alert alert-info text-center mt-3">No hay turnos registrados en este periodo.</div>
                                )}

                            </>
                        )}
                    </div>
                    <div className="modal-footer">
                        <button className="btn btn-secondary" onClick={onClose} disabled={loading}>
                            Cerrar
                        </button>
                        <button className="btn btn-danger text-white" onClick={handleDescargarPDF} disabled={loading}>
                            <i className="bi bi-file-earmark-pdf me-2"></i>PDF (Foto)
                        </button>
                        <button className="btn btn-warning text-dark" onClick={handleDescargarPDF_HTML} disabled={loading}>
                            <i className="bi bi-lightning-fill me-2"></i>PDF Ligero (HTML)
                        </button>
                        <button className="btn btn-info text-white" onClick={handleCopiarImagen} disabled={loading}>
                            <i className="bi bi-clipboard-check me-2"></i>Copiar Imágen
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default CobranzaDetalle;
